import { useDay } from "@datepicker-react/hooks";
import { useI18nContext, useI18nCurrency } from "@hopper-b2b/i18n";
import { useTrackEvent } from "@hopper-b2b/tracking";
import { Deal } from "@hopper-proto/hopper/dealfeed/proto/v1/Deal";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import { default as dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useBoolean, useLocalStorage } from "usehooks-ts";
import { CloseIcon } from "../../assets";
import { PickerType, useDateCalendarContext } from "./DateCalendarContext";
import {
  getDealCurrency,
  getDealDepartureDayjs,
  getDealPrice,
  getDealsDepartingOn,
  getDealsReturningOn,
} from "./types";

export interface DayProps {
  day: {
    dayLabel: string;
    date: Date;
  };
}

const dayStartIcon = (fill: string) =>
  `url('data:image/svg+xml,<svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg"><path id="Selector" fill-rule="evenodd" clip-rule="evenodd" d="M4.92238 0C2.20382 0 0 2.29219 0 5.13046V35.8695C0 38.703 2.20421 41 4.91875 41H24.5676C27.2841 41 30.576 38.9992 31.9153 36.5402L38.3153 24.7891C39.6568 22.326 39.6092 18.3616 38.2141 15.9432L31.5474 4.38612C30.15 1.96373 26.8124 0 24.0949 0H4.92238Z" fill="${encodeURIComponent(
    fill
  )}"/></svg>')`;
const dayEndIcon = (fill: string) =>
  `url('data:image/svg+xml,<svg width="39" height="41" viewBox="0 0 39 41" fill="none" xmlns="http://www.w3.org/2000/svg"><path id="Selector" fill-rule="evenodd" clip-rule="evenodd" d="M34.3696 0C37.0882 0 39.292 2.29219 39.292 5.13046V35.8695C39.292 38.703 37.0878 41 34.3732 41H14.7244C12.0079 41 8.71597 38.9992 7.37672 36.5402L0.976681 24.7891C-0.364788 22.326 -0.317173 18.3616 1.07788 15.9432L7.74459 4.38612C9.14196 1.96373 12.4796 0 15.1971 0H34.3696Z" fill="${encodeURIComponent(
    fill
  )}"/></svg>')`;
const todayIcon = () =>
  `url('data:image/svg+xml,${encodeURIComponent('<svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="#111111"/></svg>')}')`;

export const Day = ({ day: { dayLabel, date } }: DayProps) => {
  const { formatFiatCurrencyForShopping } = useI18nCurrency();
  const trackEvent = useTrackEvent();
  const dayRef = useRef<HTMLButtonElement>(null);
  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    isMonthVisible,
    isStartDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    pickerType,
    getBucket,
    deals,
    startDate,
    endDate,
    forceDisplayBucketsAndDeals,
  } = useDateCalendarContext();
  const {
    tabIndex,
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    onClick,
    onMouseEnter,
  } = useDay({
    date,
    dayRef,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
  });
  const isToday = dayjs().isSame(date, "day");
  const isBeforeStartDate =
    startDate && dayjs(date).isBefore(dayjs(startDate), "day");

  const startDateSelected = !!startDate;
  const endDateSelected = !!endDate;

  const allDatesAreSelected =
    pickerType === PickerType.RANGE
      ? startDateSelected && endDateSelected
      : startDateSelected;

  /**
   * Logic for displaying a deal on this day:
   * 1. If forceDisplayBucketsAndDeals is true (flight list behaviour).
   *  a. If both start and end dates are selected, show deals departing on this date.
   *  b. If only start date is selected, show deals returning on this date for deals departing on the start date.
   *  c. If no dates are selected, show deals departing on this date.
   * 2. If forceDisplayBucketsAndDeals is false (deals destination page behaviour).
   *  a. If both start and end dates are selected, show no deals.
   *  b. If only start date is selected, show deals returning on this date for deals departing on the start date.
   *  c. If no dates are selected, show deals departing on this date.
   */
  const dealForDate = useMemo(() => {
    if (allDatesAreSelected && forceDisplayBucketsAndDeals)
      return getDealsDepartingOn(deals, date)?.[0];
    if (allDatesAreSelected && !forceDisplayBucketsAndDeals) return undefined;
    if (startDateSelected)
      return getDealsReturningOn(
        getDealsDepartingOn(deals, startDate),
        date
      )?.[0];
    return getDealsDepartingOn(deals, date)?.[0];
  }, [
    forceDisplayBucketsAndDeals,
    deals,
    date,
    allDatesAreSelected,
    startDateSelected,
    startDate,
  ]);
  const hasDeal = !!dealForDate;

  // Custom onClick handler that tracks deal date taps
  const handleClick = useCallback(() => {
    // Track if this is a deal date
    if (hasDeal && dealForDate) {
      trackEvent({
        eventName: "tapped_deal_date_in_calendar",
        properties: {
          deal_id: dealForDate.dealId?.id?.value,
        },
      });
    }
    onClick();
  }, [hasDeal, dealForDate, onClick, trackEvent]);

  const cellColor = (tokens: Theme["tokens"]) => {
    if (disabledDate) {
      return {};
    }

    const selectedStyles =
      (isWithinHoverRange || isSelected) && pickerType === PickerType.RANGE
        ? {
            "&, &:hover": {
              backgroundColor: tokens.component.calendar.surfaceRange,
              color: tokens.component.calendar.textRange,
              border:
                (!allDatesAreSelected || forceDisplayBucketsAndDeals) && hasDeal
                  ? `2px solid ${tokens.component.calendar.textRange}`
                  : undefined,
            },
          }
        : {};

    if ((!allDatesAreSelected || forceDisplayBucketsAndDeals) && hasDeal) {
      return {
        backgroundColor: tokens.component.calendar.surfaceGood,
        color: tokens.component.calendar.textGood,
        border: `2px solid ${tokens.component.calendar.textGood}`,
        ...selectedStyles["&, &:hover"],
      };
    }

    switch (
      (allDatesAreSelected || isBeforeStartDate) && !forceDisplayBucketsAndDeals
        ? undefined
        : getBucket(date)
    ) {
      case 0:
        return {
          backgroundColor: tokens.component.calendar.surfaceGood,
          color: tokens.component.calendar.textGood,
          ...selectedStyles,
        };
      case 1:
        return {
          backgroundColor: tokens.component.calendar.surfaceAverage,
          color: tokens.component.calendar.textAverage,
          ...selectedStyles,
        };
      case 2:
        return {
          backgroundColor: tokens.component.calendar.surfaceBad,
          color: tokens.component.calendar.textBad,
          ...selectedStyles,
        };
      case 3:
        return {
          backgroundColor: tokens.component.calendar.surfaceTerrible,
          color: tokens.component.calendar.textTerrible,
          ...selectedStyles,
        };
      default:
        return {
          backgroundColor: tokens.component.calendar.surfaceDefault,
          color: tokens.component.calendar.textDefault,
          ...selectedStyles,
        };
    }
  };

  function formatDealPrice(deal: Deal): string {
    const price = getDealPrice(deal);
    const currency = getDealCurrency(deal);

    if (!price) return "";

    return (
      formatFiatCurrencyForShopping({
        value: price,
        currencyCode: currency,
      }) ?? ""
    );
  }

  const price = dealForDate ? formatDealPrice(dealForDate) : "";

  const dayContent = (
    <Button
      variant="secondary"
      size="day"
      ref={dayRef}
      disabled={disabledDate}
      data-testid={`calendar-day-${dayLabel}`}
      sx={({ tokens }) => ({
        transition: "none",
        ...(!dayLabel && { visibility: "hidden" }),
        ...cellColor(tokens),
        ...(isSelectedStartOrEnd
          ? {
              "&, &:hover": {
                backgroundColor:
                  pickerType === PickerType.RANGE
                    ? tokens.component.calendar.surfaceRange
                    : tokens.component.calendar.surfaceSelected,
                color: tokens.primary.contrast,
                ...(pickerType === PickerType.RANGE && {
                  backgroundImage: isStartDate(date)
                    ? dayStartIcon(tokens.component.calendar.surfaceSelected)
                    : dayEndIcon(tokens.component.calendar.surfaceSelected),
                  backgroundPosition: isStartDate(date) ? "right" : "left",
                  backgroundSize: "cover",
                }),
              },
            }
          : {
              backgroundImage: isToday ? todayIcon() : undefined,
              backgroundPosition: "bottom 8px center",
              backgroundSize: "5px 5px",
              backgroundRepeat: "no-repeat",
            }),
      })}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      tabIndex={tabIndex}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 0.25,
        }}
      >
        <Typography variant="labelSmall" component="span">
          {dayLabel}
        </Typography>
        {(!allDatesAreSelected || forceDisplayBucketsAndDeals) &&
        hasDeal &&
        price ? (
          <Typography variant="labelExtraSmall" component="span">
            {price}
          </Typography>
        ) : null}
      </Box>
    </Button>
  );

  const isFirstVisibleDeal = useMemo(() => {
    const dealForDateDepartureDay = dealForDate
      ? getDealDepartureDayjs(dealForDate)
      : undefined;
    const sameMonthDeals = deals.filter((d) =>
      getDealDepartureDayjs(d)?.isSame(dealForDateDepartureDay, "month")
    );
    const previousMonth = dealForDateDepartureDay?.subtract(1, "month");
    const isCurrentMonthVisible =
      !!dealForDateDepartureDay &&
      isMonthVisible(
        dealForDateDepartureDay.year(),
        dealForDateDepartureDay.month()
      );
    const isPreviousMonthVisible =
      !!previousMonth &&
      isMonthVisible(previousMonth.year(), previousMonth.month());
    const isFirstMonthVisible =
      isCurrentMonthVisible && !isPreviousMonthVisible;
    const isFirstDealOfTheMonth =
      !!dealForDate && sameMonthDeals.indexOf(dealForDate) === 0;
    return isFirstMonthVisible && isFirstDealOfTheMonth;
  }, [dealForDate, deals, isMonthVisible]);

  return isFirstVisibleDeal ? (
    <DealTooltip>{dayContent}</DealTooltip>
  ) : (
    dayContent
  );
};

function DealTooltip({ children }: { children: React.ReactElement }) {
  const { t } = useI18nContext();
  const [dismissed, setDismissed] = useLocalStorage(
    "dealCalendarTooltipDismissed",
    false
  );
  const { value: isOpen, setFalse: close, setTrue: open } = useBoolean(false);
  const closeAndDismiss = useCallback(() => {
    setDismissed(true);
    close();
  }, [setDismissed, close]);

  useEffect(() => {
    if (dismissed) return;
    if (isOpen) return;

    const timeoutId = setTimeout(open, 500);
    return () => clearTimeout(timeoutId);
  }, [dismissed, open, isOpen]);

  useEffect(() => {
    return () => {
      closeAndDismiss();
    };
  }, [closeAndDismiss]);

  return (
    <DarkTooltip
      arrow
      placement="top"
      open={isOpen && !dismissed}
      title={
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={200}
        >
          <Typography variant="bodyMedium">
            {t("hopperpro.dealCalendarTooltip")}
          </Typography>
          <IconButton
            onClick={closeAndDismiss}
            sx={{
              color: ({ tokens }) => tokens.colors.gray[10],
              "&:hover": {
                opacity: 0.8,
                color: ({ tokens }) => tokens.colors.gray[40],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      }
    >
      {children}
    </DarkTooltip>
  );
}

const DarkTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }: { theme: Theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.tokens.colors.gray[90],
    color: theme.tokens.colors.gray[10],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.tokens.colors.gray[90],
  },
}));
